// eslint-disable-next-line no-unused-vars
import React, { useMemo } from 'react';
import { string, array, node, object, func, bool } from 'prop-types';
import {
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
} from '@carbon/react';
import styled from '@emotion/styled';

import ToolbarHeader from 'Containers/Products/ToolbarHeader';
import ToolbarFooter from 'Containers/Products/ToolbarFooter';
import PromotionBanner from 'Containers/Products/PromotionBanner';
import AddToCart from 'Components/AddToCart/AddToCart';
import ProductsUnavailableModalComponent from './ProductsUnavailableModal';

const StyledProductsContainer = styled.div`
  position: relative;
  width: 100%;
  flex-grow: 1;
  min-width: 0;
  margin-top: ${ props => !props.hasActiveCategory ? '43px' : 0 };

  .cds--data-table-content {
    overflow-x: visible;
    margin-top: 10px;
  }

  .overlay {
    cursor: progress;
  }

  .noResultsContainer {
    .title {
      width: 100%;
      font-size: 1.75rem;
      font-weight: 600;
      border-bottom: 1px solid ${ props => props.theme.products.colors.border };
      padding-bottom: 10px;
      margin-bottom: 15px;

      span {
        color: ${ props => props.theme.colors.default };
      }
    }

    > p {
      font-weight: 600;
    }

    p, li {
      color: ${ props => props.theme.colors.darkGray };
      font-size: 0.875rem;
    }

    li {
      margin: 5px 0 5px 13px;
      list-style-type: disc;
    }
  }

  > .title {
    margin-bottom: 10px;
    font-weight: 600;
    font-size: 1.75em;
    color: ${ props => props.theme.colors.darkGray };
  }

  .headingRow {
    background-color: ${ props => props.theme.colors.shadowGray };
  }

  .viewMode {
    &.table {
      table-layout: fixed;
      margin-top: 25px;
    }

    &.grid {
      display: grid;
      width: calc(100% + 20px);
      margin-left: -10px;
      grid-gap: 2em;
      padding: 10px;
      grid-template-columns: repeat(1, minmax(0, 1fr));

      @media screen and (min-width: 480px) and (max-width: 1055px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        grid-gap: 1em;
      }

      @media screen and (min-width: 1056px) and (max-width: 1439px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }

      @media screen and (min-width: 1440px) {
        grid-template-columns: repeat(4, minmax(0, 1fr));
      }

      .skeleton {
        flex-direction: column;

        > div {
          &:last-of-type {
            display: none;
          }

          max-width: 100%;
          width: 100%;
          margin: 10px auto;
        }
      }
    }

    &.list {
      .skeleton {
        padding: 10px 0;
      }
    }
  }

  @media screen and (max-width: 767px) {
    > .title {
      text-align: center;
    }
  }
`;

const StyledTableHead = styled(TableHead)`
  background-color: ${ props => props.theme.colors.shadowGray };
  font-weight: 600;
  font-size: 1em;
  color: ${ props => props.theme.colors.darkGray };
  text-align: center;

  .cds--table-header-label {
    text-align: center;
  }

  th {
    text-align: center !important;
    padding: 0;
  }

  th.thumbnail {
    width: 70px;
    min-width: 70px;
    padding: 0;
  }

  th.code {
    width: 18%;
  }

  th.name {
    width: 33%;
  }

  th.stock {
    width: 5%;
  }

  th.price {
    width: 11%;
  }

  th.promotions {
    width: 11%;
  }

  th.quantity {
    width: 15%;
  }

  th.details {
    width: 8%;
  }
`;

const StyledTableBody = styled(TableBody)`
  background-color: transparent !important;
`;

const StyledButtonContainer = styled.div`
  padding: 30px 0 15px;
  display: flex;
  justify-content: center;

  button {
    &:disabled {
      opacity: 1;
      background-color: #c6c6c6;
    }
  }
`;

export const StyledOverlay = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  display: ${ props => props.open ? 'flex' : 'none' };
  z-index: 12;
  background-color: rgba(255, 255, 255, 0.5);
`;

const Products = props => {
  const {
    t,
    onAddToCart,
    addToCartLoading,
    productsLoading,
    children,
    products,
    quantities,
    activeFilters,
    viewMode,
    theme,
    isRecentlyAdded,
    pricesAvailableToday,
    setMobileFiltersOpen
    // error
  } = props;

  const headers = useMemo(() => [{
    key: 'thumbnail',
    header: '',
  }, {
    key: 'code',
    header: t('product.code'),
  }, {
    key: 'name',
    header: t('product.name'),
  }, {
    key: 'stock',
    header: t('product.stock'),
  }, {
    key: 'price',
    header: t('product.price'),
  }, {
    key: 'promotions',
    header: t('product.promotions'),
  }, {
    key: 'quantity',
    header: t('product.quantity'),
  }, {
    key: 'details',
    header: t('product.details'),
  }], []);

  const TableView = useMemo(() => (
    <>
      <Table className="viewMode tableView">
        <StyledTableHead theme={ theme }>
          <TableRow>
            { headers.map(header => (
              <TableHeader
                className={ header.key }
                key={ header.key }
              >
                { header.header }
              </TableHeader>
            )) }
          </TableRow>
        </StyledTableHead>
        <StyledTableBody>
          { children }
        </StyledTableBody>
      </Table>
      <StyledButtonContainer>
        <AddToCart
          disabled={
            addToCartLoading
            || !Object.values(quantities).filter(q => q >= 1).length
          }
          loading={ addToCartLoading }
          size="md"
          onClick={ onAddToCart }
          trackingContext={ {
            location: 'product_table_view',
            products: { ...quantities }
          } }
          showSelector={ false }
          data-test="onAddToCartProductListView"
          data-testid="onAddToCartProductListView"
        />
      </StyledButtonContainer>
    </>
  ), [
    theme,
    headers,
    children,
    addToCartLoading,
    quantities,
    onAddToCart,
  ]);

  return (
    // TODO: offer request button
    <StyledProductsContainer
      hasActiveCategory={
        (products.length > 0 || productsLoading)
        && activeFilters?.category?.name
      }
    >
      <PromotionBanner />
      {!products.length && (isRecentlyAdded || pricesAvailableToday) && <ProductsUnavailableModalComponent isRecentlyAdded={ isRecentlyAdded } pricesAvailableToday={ pricesAvailableToday } />}
      { (
        (products.length > 0 || productsLoading)
        && activeFilters?.category?.name
      ) && (
        <h1 className="title">{ activeFilters?.category?.name }</h1>
      ) }
      <ToolbarHeader viewMode={ viewMode } setMobileFiltersOpen={ setMobileFiltersOpen } />
      { (
        !productsLoading
        && Object.keys(activeFilters || {}).length > 0
        && products.length === 0
      ) && (
        <div className="noResultsContainer">
          <h3
            className="title"
            dangerouslySetInnerHTML={ {
              __html: t('products.noResultsFor', {
                search: activeFilters?.search?.length
                  ? `"${ activeFilters.search.join(' ') }"`
                  : t('filters.selectedFilters'),
              }),
            } }
          />
          <p>{ t('products.noProductsAvailableAdvice') }</p>
          <ul>
            <li>{ t('products.searchAdviceUseLessFilters') }</li>
            <li>{ t('products.searchAdviceCorrectTerm') }</li>
            <li>{ t('products.searchAdviceGenericTerm') }</li>
          </ul>
        </div>
      ) }
      {/* eslint-disable-next-line no-nested-ternary */ }
      { products.length > 0 || productsLoading
        ? (
          viewMode === 'table'
            ? TableView
            : (
              <div className={ `viewMode ${ viewMode }` }>
                { children }
              </div>
            )
        )
        : null
      }
      { (productsLoading || products.length > 0) && <ToolbarFooter /> }
    </StyledProductsContainer>
  );
};

Products.propTypes = {
  activeFilters: object,
  viewMode: string,
  onAddToCart: func,
  products: array,
  theme: object,
  t: func,
  quantities: object,
  children: node,
  productsLoading: bool,
  addToCartLoading: bool,
  isRecentlyAdded: bool,
  pricesAvailableToday: bool,
  setMobileFiltersOpen: func,
};

Products.displayName = 'ProductsComponent';

export default Products;
